import { useState } from "react";
import { bedLabel, imageUrl, money, num } from "../format";
import type { CardListing, Listing } from "../../../shared/types";

export function Photo({ id, width, alt, className }: { id: string; width: number; alt: string; className?: string }) {
  const [state, setState] = useState<"loading" | "loaded" | "failed">("loading");
  if (state === "failed") return null;
  return (
    <img
      className={[className, state === "loaded" ? "loaded" : ""].filter(Boolean).join(" ")}
      src={imageUrl(id, width)}
      alt={alt}
      loading="lazy"
      decoding="async"
      onLoad={() => setState("loaded")}
      onError={() => setState("failed")}
    />
  );
}

export function ListingCard({
  listing,
  index = 0,
  onOpen,
}: {
  listing: Listing | CardListing;
  index?: number;
  onOpen: (id: string) => void;
}) {
  const reasons = "reasons" in listing ? listing.reasons : [];
  const caveats = "caveats" in listing ? listing.caveats : [];

  return (
    // A real href so the card can be opened in a new tab or copied; a plain
    // click is intercepted to keep the conversation alive.
    <a
      className="card"
      href={`/listing/${listing.id}`}
      style={{ animationDelay: `${Math.min(index, 5) * 55}ms` }}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        onOpen(listing.id);
      }}
    >
      <div className="card-media">
        <Photo id={listing.images[0]} width={640} alt={listing.title} />
        <div className="price-pill">
          {money(listing.price)}
          <span>/mo</span>
        </div>
        <div className="type-pill">{listing.propertyType}</div>
      </div>

      <div className="card-body">
        <h3 className="card-title">{listing.title}</h3>
        <div className="card-loc">
          {listing.neighborhood} · Walk Score {listing.walkScore}
        </div>
        <div className="card-meta">
          <span>{bedLabel(listing.beds)}</span>
          <span>{listing.baths} ba</span>
          <span>{num(listing.sqft)} sqft</span>
        </div>

        {(reasons.length > 0 || caveats.length > 0) && (
          <div className="tags">
            {reasons.slice(0, 3).map((r) => (
              <span className="tag" key={r}>
                {r}
              </span>
            ))}
            {caveats.slice(0, 2).map((c) => (
              <span className="tag warn" key={c}>
                {c}
              </span>
            ))}
          </div>
        )}
      </div>
    </a>
  );
}

export function CardSkeleton({ index }: { index: number }) {
  return (
    <div className="card skeleton" style={{ animationDelay: `${index * 70}ms` }}>
      <div className="card-media shimmer" />
      <div className="card-body">
        <div className="shimmer line" style={{ width: "82%" }} />
        <div className="shimmer line" style={{ width: "54%" }} />
        <div className="shimmer line" style={{ width: "68%" }} />
      </div>
    </div>
  );
}
